import { Controller } from "@hotwired/stimulus";

type TomSelectLike = {
  options?: Record<string, { name?: string; text?: string; email?: string }>;
  clear?: (silent?: boolean) => void;
  close?: () => void;
  blur?: () => void;
};

type TomSelectAddEvent = Event & {
  detail: {
    value?: string;
    tomSelect?: TomSelectLike;
  };
};

type MemberActionEvent = Event & {
  params: {
    memberId?: string | number;
  };
};

export default class extends Controller {
  static targets = ["members", "emptyState"];
  static values = {
    removeLabel: String,
    // The hidden field each picked person is submitted under. Defaults to the
    // group form this controller was written for; the event wizard reuses it
    // for `event[assigned_user_ids][]`.
    fieldName: { type: String, default: "group[member_ids][]" },
  };

  declare readonly membersTarget: HTMLElement;
  declare readonly emptyStateTarget: HTMLElement;
  declare readonly hasEmptyStateTarget: boolean;
  declare readonly removeLabelValue: string;
  declare readonly fieldNameValue: string;

  add(event: TomSelectAddEvent) {
    const id = String(event.detail.value || "");
    const tomSelect = event.detail.tomSelect as TomSelectLike | undefined;

    if (!id || this.hasMember(id)) {
      this.resetPicker(tomSelect);
      return;
    }

    const option = tomSelect?.options?.[id] || {};
    const name = option.name || option.text || "";
    const email = option.email || "";

    this.membersTarget.insertAdjacentHTML(
      "beforeend",
      this.memberHtml(id, name, email),
    );
    this.updateEmptyState();
    this.resetPicker(tomSelect);
  }

  remove(event: MemberActionEvent) {
    const id = String(event.params.memberId || "");
    const member = Array.from(
      this.element.querySelectorAll<HTMLElement>(
        "[data-group-member-picker-member-id]",
      ),
    ).find((element) => element.dataset.groupMemberPickerMemberId === id);

    member?.remove();
    this.updateEmptyState();
  }

  focusPicker() {
    window.setTimeout(() => {
      this.pickerInput()?.focus();
    }, 0);
  }

  private hasMember(id: string) {
    return Array.from(
      this.element.querySelectorAll<HTMLInputElement>(
        `input[name="${this.fieldNameValue}"]`,
      ),
    ).some((input) => input.value === id);
  }

  private memberHtml(id: string, name: string, email: string) {
    const displayName = name || email;
    const initials = this.initials(displayName, email);

    return `
      <div
        id="group_member_${this.escapeAttribute(id)}"
        class="flex items-center justify-between p-2 border rounded-lg bg-gray-50"
        data-group-member-picker-member-id="${this.escapeAttribute(id)}"
      >
        <div class="flex items-center gap-2">
          <div class="avatar">
            <div class="w-8 h-8 rounded-full outline outline-2 outline-offset outline-[#EFEFEF]">
              <span class="bg-[#1C2E30] text-white flex items-center justify-center rounded-full h-full font-normal text-sm">${this.escapeHtml(initials)}</span>
            </div>
          </div>
          <div class="flex flex-row items-center align-middle">
            <span class="font-medium text-sm">${this.escapeHtml(displayName)}</span>
            <span class="text-xxs text-gray-500 ml-2">${this.escapeHtml(email)}</span>
          </div>
          <input type="hidden" name="${this.escapeAttribute(this.fieldNameValue)}" value="${this.escapeAttribute(id)}">
        </div>
        <button
          type="button"
          class="text-gray-500 hover:text-red-500 transition-colors cursor-pointer"
          aria-label="${this.escapeAttribute(this.removeLabelValue)}"
          data-action="group-member-picker#remove"
          data-group-member-picker-member-id-param="${this.escapeAttribute(id)}"
        >
          <span class="block w-4 h-4 leading-4 text-center">x</span>
        </button>
      </div>
    `;
  }

  private initials(name: string, email: string) {
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");

    return initials || email[0]?.toUpperCase() || "?";
  }

  private resetPicker(tomSelect?: TomSelectLike) {
    tomSelect?.clear?.(true);
    tomSelect?.close?.();
    tomSelect?.blur?.();
  }

  private pickerInput() {
    return this.element.querySelector<HTMLInputElement>(
      ".dropdown-content .ts-control input",
    );
  }

  private updateEmptyState() {
    if (!this.hasEmptyStateTarget) return;

    this.emptyStateTarget.classList.toggle(
      "hidden",
      this.membersTarget.children.length > 0,
    );
  }

  private escapeHtml(value: string) {
    return value.replace(/[&<>"']/g, (char) => {
      const entities: Record<string, string> = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      };

      return entities[char];
    });
  }

  private escapeAttribute(value: string) {
    return this.escapeHtml(value);
  }
}
